import React, { useState, useEffect, useReducer, useRef } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { Clock, Check, CheckCheck, Ban, ChevronDown, Download, Bot, Sparkles, RefreshCw, Loader2, FileText, AlertTriangle } from "lucide-react";
import { usePendingMessages } from "../../context/PendingMessages/PendingMessagesContext";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import StickerPreview from "../StickerPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    // Used for pagination (loading older pages): new messages go to the TOP
    const messages = action.payload;
    const existingIds = new Set(state.map(m => m.id));
    const olderMessages = [];
    const updated = [];
    messages.forEach((message) => {
      if (existingIds.has(message.id)) {
        updated.push(message);
      } else {
        olderMessages.push(message);
      }
    });
    const merged = state.map((m) => {
      const u = updated.find(u => u.id === m.id);
      return u || m;
    });
    return [...olderMessages, ...merged];
  }
  if (action.type === "LOAD_MESSAGES_APPEND") {
    // Used on reconnect: fetch page 1 again — new messages go to the BOTTOM
    const messages = action.payload;
    const existingIds = new Set(state.map(m => m.id));
    const newerMessages = [];
    const updated = [];
    messages.forEach((message) => {
      if (existingIds.has(message.id)) {
        updated.push(message);
      } else {
        newerMessages.push(message);
      }
    });
    const merged = state.map((m) => {
      const u = updated.find(u => u.id === m.id);
      return u || m;
    });
    return [...merged, ...newerMessages];
  }
  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const idx = state.findIndex((m) => m.id === newMessage.id);
    if (idx !== -1) {
      const newState = [...state];
      newState[idx] = newMessage;
      return newState;
    }
    return [...state, newMessage];
  }
  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const idx = state.findIndex((m) => m.id === messageToUpdate.id);
    if (idx !== -1) {
      const newState = [...state];
      newState[idx] = messageToUpdate;
      return newState;
    }
    return state;
  }
  if (action.type === "DELETE_MESSAGE") {
    const messageId = action.payload;
    return state.filter(m => m.id !== messageId);
  }
  if (action.type === "RESET") return [];
  return state;
};

// Un mensaje propio sin confirmar (ack 0) puede estar en camino: el ack del
// servidor de WhatsApp normalmente llega en menos de un segundo. Pasado este
// tiempo, ya no es "en camino" sino "WhatsApp nunca lo confirmó", y hay que
// decírselo al agente en vez de dejarle un reloj indefinido: el backend lo
// está reintentando, pero puede que no haya salido.
const UNCONFIRMED_AFTER_MS = 3 * 60 * 1000;

const AckIcon = ({ ack, createdAt, isPending }) => {
  if (ack === 0) {
    const stale =
      !isPending &&
      createdAt &&
      Date.now() - new Date(createdAt).getTime() > UNCONFIRMED_AFTER_MS;

    if (stale) {
      return (
        <AlertTriangle
          className="w-3 h-3 inline ml-1 text-amber-400"
          title="WhatsApp no confirmó este mensaje. Se está reintentando; puede que no haya llegado."
        />
      );
    }
    return <Clock className="w-3 h-3 inline ml-1 text-slate-400" />;
  }
  if (ack === 1) return <Check className="w-3 h-3 inline ml-1 text-slate-400" />;
  if (ack === 2) return <CheckCheck className="w-3 h-3 inline ml-1 text-slate-400" />;
  if (ack === 3 || ack === 4) return <CheckCheck className="w-3 h-3 inline ml-1 text-blue-400" />;
  return null;
};

// Media types stored without a file (failed download) that an agent can
// manually retry against the live QR session
const RETRYABLE_MEDIA = ["image", "video", "audio", "ptt", "document", "sticker"];

const MessagesList = ({ ticketId, isGroup, channel }) => {
  const { pendingMessages, removePendingByBody } = usePendingMessages();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [retryingIds, setRetryingIds] = useState(new Set());
  const [transcribingIds, setTranscribingIds] = useState(new Set());

  const handleTranscribe = async (message) => {
    setTranscribingIds((prev) => new Set(prev).add(message.id));
    try {
      const { data } = await api.post(`/messages/${message.id}/transcribe`);

      // El backend responde 200 con el motivo para poder decirle al agente
      // qué pasó; la transcripción en sí llega por socket y actualiza la burbuja.
      if (data.reason === "ok") {
        toast.success("Nota de voz transcrita");
      } else if (data.reason === "unsupported") {
        toast.warn(
          `${data.provider} no transcribe audio. Cambia el modelo de transcripción o el proveedor de IA en Bot › Configuración.`
        );
      } else if (data.reason === "no-provider") {
        toast.warn("Esta conexión no tiene un proveedor de IA configurado.");
      } else if (data.reason === "no-media") {
        toast.warn("El audio no está disponible en el servidor. Reintenta la descarga primero.");
      } else {
        toast.warn("No se pudo transcribir la nota. Revisa los logs del proveedor de IA.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setTranscribingIds((prev) => {
        const next = new Set(prev);
        next.delete(message.id);
        return next;
      });
    }
  };

  const handleRetryMedia = async (message) => {
    setRetryingIds((prev) => new Set(prev).add(message.id));
    try {
      const { data } = await api.post(`/messages/${message.id}/retry-media`);
      if (data.recovered) {
        toast.success("Archivo recuperado correctamente");
      } else {
        toast.warn("WhatsApp ya no tiene disponible este archivo. Pide al cliente que lo reenvíe.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(message.id);
        return next;
      });
    }
  };
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/messages/" + ticketId, { params: { pageNumber } });
        if (currentTicketId.current === ticketId) {
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          setHasMore(data.hasMore);
          setLoading(false);
        }
        if (pageNumber === 1 && data.messages.length > 1) setTimeout(scrollToBottom, 100);
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    const joinRoom = () => {
      socket.emit("joinChatBox", ticketId);
    };

    const handleReconnect = async () => {
      joinRoom();
      try {
        const { data } = await api.get("/messages/" + ticketId, { params: { pageNumber: 1 } });
        if (currentTicketId.current === ticketId) {
          dispatch({ type: "LOAD_MESSAGES_APPEND", payload: data.messages });
          setTimeout(scrollToBottom, 100);
        }
      } catch (_) {}
    };

    const handleAppMessage = (data) => {
      if (data.action === "create" && data.message.ticketId === +ticketId) {
        if (data.message.fromMe) removePendingByBody(data.message.body, ticketId);
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        setTimeout(scrollToBottom, 100);
      }
      if (data.action === "update" && data.message.ticketId === +ticketId) {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
      if (data.action === "delete" && data.messageId) {
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    };

    socket.on("connect", joinRoom);
    socket.on("reconnect", handleReconnect);
    socket.on("appMessage", handleAppMessage);

    if (socket.connected) {
      joinRoom();
    }

    return () => {
      socket.off("connect", joinRoom);
      socket.off("reconnect", handleReconnect);
      socket.off("appMessage", handleAppMessage);
    };
  }, [ticketId]);

  const scrollToBottom = () => lastMessageRef.current?.scrollIntoView({});

  const handleScrollToMessage = (messageId) => {
    if (!messageId) return;
    const element = document.getElementById(`message-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      const bubble = element.querySelector(".rounded-2xl");
      if (bubble) {
        bubble.classList.add("ring-4", "ring-indigo-400/70", "dark:ring-indigo-500/70", "scale-[1.03]", "transition-all", "duration-300");
        setTimeout(() => {
          bubble.classList.remove("ring-4", "ring-indigo-400/70", "dark:ring-indigo-500/70", "scale-[1.03]");
        }, 1000);
      }
    } else {
      toast.info("El mensaje original está muy arriba. Desplázate hacia arriba para cargarlo.");
    }
  };

  useEffect(() => {
    const mine = pendingMessages.filter(m => m.ticketId === +ticketId);
    if (mine.length > 0) setTimeout(scrollToBottom, 50);
  }, [pendingMessages, ticketId]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop } = e.currentTarget;
    if (scrollTop === 0) document.getElementById("messagesList").scrollTop = 1;
    if (scrollTop < 50) setPageNumber((p) => p + 1);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body && message.body.split("|").length >= 2) {
      const parts = message.body.split("|");
      return <LocationPreview image={parts[0]} link={parts[1]} description={parts[2] || null} />;
    }
    if (message.mediaType === "vcard") {
      const body = message.body || "";
      let contact = "";
      const fnMatch = body.match(/^FN[^:]*:(.+)$/m);
      if (fnMatch) contact = fnMatch[1].trim();
      let phoneNumber = "";
      const telMatch = body.match(/^TEL[^:]*:(.+)$/m);
      if (telMatch) phoneNumber = telMatch[1].trim();
      if (!phoneNumber) { const plusMatch = body.match(/(\+\d[\d\s\-().]{6,})/m); if (plusMatch) phoneNumber = plusMatch[1].trim(); }
      return <VcardPreview contact={contact} numbers={phoneNumber} />;
    }
    if (message.mediaType === "call_log") {
      return (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-950/20 rounded-lg text-sm">
          <span className="text-lg">📞</span>
          <span className="text-slate-700 dark:text-slate-300">{message.body}</span>
        </div>
      );
    }
    if (message.mediaUrl && (message.mediaType === "sticker" || /\.webp$/i.test(message.mediaUrl)))
      return <StickerPreview imageUrl={message.mediaUrl} />;
    if (/^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) && message.mediaType === "image")
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    if (message.mediaType === "audio" || message.mediaType === "ptt") {
      const transcribing = transcribingIds.has(message.id);
      return (
        <div className="flex flex-col gap-1.5 min-w-[220px]">
          <Audio url={message.mediaUrl} isLeft={!message.fromMe} />
          {message.transcription && (
            <div className="mt-1 text-xs text-slate-700 dark:text-slate-300 italic border-l-2 border-indigo-400 pl-2 py-0.5 max-w-sm whitespace-pre-wrap">
              <span className="font-semibold text-[10px] text-indigo-500 not-italic block uppercase tracking-wider mb-0.5">Transcripción por IA</span>
              {message.transcription}
            </div>
          )}
          {/* Transcripción a demanda: útil con la automática apagada, cuando
              falló, o en audios anteriores a activarla. */}
          {!message.fromMe && message.mediaUrl && (
            <button
              onClick={() => handleTranscribe(message)}
              disabled={transcribing}
              className="self-start flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors disabled:opacity-50"
            >
              {transcribing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <FileText className="w-3 h-3" />
              )}
              {transcribing
                ? "Transcribiendo..."
                : message.transcription
                ? "Rehacer transcripción"
                : "Transcribir nota"}
            </button>
          )}
        </div>
      );
    }
    if (message.mediaType === "video")
      return <video className="w-64 h-48 rounded-xl object-cover" src={message.mediaUrl} controls />;
    return (
      <div className="flex justify-center p-3">
        <a
          href={message.mediaUrl}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 px-4 py-2 border border-indigo-300 text-indigo-600 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors"
        >
          <Download className="w-4 h-4" />
          Download
        </a>
      </div>
    );
  };

  const renderDailyTimestamps = (message, index) => {
    const showDate = index === 0 || !isSameDay(parseISO(messagesList[index].createdAt), parseISO(messagesList[index - 1].createdAt));
    if (!showDate) return null;
    return (
      <div key={`ts-${message.id}`} className="flex justify-center my-3">
        <span className="px-3 py-1 bg-white/70 dark:bg-slate-800/70 backdrop-blur-sm text-xs text-slate-500 dark:text-slate-400 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
          {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
        </span>
      </div>
    );
  };

  const renderMessageDivider = (message, index) => {
    if (index > 0 && messagesList[index].fromMe !== messagesList[index - 1].fromMe)
      return <div key={`div-${message.id}`} className="mt-3" />;
    return null;
  };

  const renderQuotedMessage = (message) => (
    <div 
      onClick={() => handleScrollToMessage(message.quotedMsg?.id)}
      className={`flex rounded-lg mb-2 overflow-hidden cursor-pointer hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors select-none ${
        message.fromMe ? "bg-indigo-100/50 dark:bg-indigo-900/30" : "bg-slate-100 dark:bg-slate-700/50"
      }`}
    >
      <span className={`w-1 flex-shrink-0 ${message.quotedMsg?.fromMe ? "bg-indigo-500" : "bg-sky-400"}`} />
      <div className="px-2 py-1.5 max-w-xs">
        {!message.quotedMsg?.fromMe && (
          <p className="text-xs font-semibold text-sky-500 mb-0.5">{message.quotedMsg?.contact?.name}</p>
        )}
        <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{message.quotedMsg?.body}</p>
      </div>
    </div>
  );

  const renderMessages = () =>
    messagesList.map((message, index) => {
      const isLeft = !message.fromMe;
      return (
        <React.Fragment key={message.id}>
          {renderDailyTimestamps(message, index)}
          {renderMessageDivider(message, index)}
          <div id={`message-${message.id}`} className={`group relative mt-0.5 max-w-[75%] min-w-[100px] ${isLeft ? "self-start" : "self-end ml-5"}`}>
            <button
              className="absolute -top-1 right-0 p-0.5 rounded-full bg-white/80 dark:bg-slate-800/80 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10 disabled:hidden"
              disabled={message.isDeleted}
              onClick={(e) => { setAnchorEl(e.currentTarget); setSelectedMessage(message); }}
            >
              <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
            </button>

            <div className={`rounded-2xl px-3 pt-2 pb-1.5 shadow-sm ${
              message.mediaType === "note"
                ? "bg-amber-100 dark:bg-amber-950/30 text-slate-800 dark:text-slate-200 border-l-4 border-amber-500 rounded-tr-sm"
                : isLeft
                  ? "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-sm"
                  : message.mediaType === "schedule_auto"
                    ? "bg-amber-500 text-white rounded-tr-sm"
                    : message.mediaType === "bot_auto"
                      ? "bg-emerald-600 text-white rounded-tr-sm"
                      : message.mediaType === "bot_ai"
                        ? "bg-violet-600 text-white rounded-tr-sm"
                        : "bg-indigo-600 text-white rounded-tr-sm"
            }`}>
              {message.mediaType === "note" && (
                <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 mb-1 flex items-center gap-1">
                  📌 Nota interna por {message.user?.name || "Agente"}
                </p>
              )}
              {!isLeft && !["note", "schedule_auto", "bot_auto", "bot_ai"].includes(message.mediaType) && (
                <>
                  {message.fromMobile && (
                    <p className="text-[9px] font-semibold text-indigo-200 mb-1 flex items-center gap-1 select-none">
                      📱 Fuera de plataforma
                    </p>
                  )}
                  {!message.fromMobile && message.userId && (
                    <p className="text-[9px] font-semibold text-indigo-200 mb-1 flex items-center gap-1 select-none">
                      💻 {message.user?.name || "Agente"}
                    </p>
                  )}
                </>
              )}
              {message.mediaType === "schedule_auto" && (
                <p className="text-[10px] font-semibold text-amber-100 mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Respuesta automática · Fuera de horario
                </p>
              )}
              {message.mediaType === "bot_auto" && (
                <p className="text-[10px] font-semibold text-emerald-100 mb-1 flex items-center gap-1">
                  <Bot className="w-3 h-3" />
                  Bot automático
                </p>
              )}
              {message.mediaType === "bot_ai" && (
                <p className="text-[10px] font-semibold text-violet-100 mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Inteligencia Artificial
                </p>
              )}
              {isGroup && isLeft && (
                <p className="text-xs font-semibold text-sky-400 mb-1">{message.contact?.name}</p>
              )}
              {(message.mediaUrl || ["location", "vcard", "call_log"].includes(message.mediaType)) && checkMessageMedia(message)}
              <div className={`relative pr-14 ${message.isDeleted ? "italic opacity-50" : ""}`}>
                {message.isDeleted && <Ban className="w-3.5 h-3.5 inline mr-1 opacity-50" />}
                {message.quotedMsg && renderQuotedMessage(message)}
                {(!["call_log", "vcard", "ptt", "audio"].includes(message.mediaType) ||
                  !message.mediaUrl) && (
                  <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                    <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  </div>
                )}
                {!message.mediaUrl && RETRYABLE_MEDIA.includes(message.mediaType) && (
                  <button
                    onClick={() => handleRetryMedia(message)}
                    disabled={retryingIds.has(message.id)}
                    className="mt-1.5 flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors disabled:opacity-50"
                  >
                    {retryingIds.has(message.id) ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3 h-3" />
                    )}
                    Reintentar descarga
                  </button>
                )}
                <span className={`absolute bottom-0 right-0 text-[10px] flex items-center gap-0.5 ${
                  isLeft ? "text-slate-400 dark:text-slate-500"
                  : message.mediaType === "note" ? "text-slate-500 dark:text-slate-400"
                  : message.mediaType === "schedule_auto" ? "text-amber-100"
                  : message.mediaType === "bot_auto" ? "text-emerald-100"
                  : message.mediaType === "bot_ai" ? "text-violet-100"
                  : "text-indigo-200"
                }`}>
                  {format(parseISO(message.createdAt), "HH:mm")}
                  {message.mediaType !== "note" && !isLeft && (
                    <AckIcon
                      ack={message.ack}
                      createdAt={message.createdAt}
                      isPending={message.isPending}
                    />
                  )}
                </span>
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    });

  return (
    <div className="relative flex flex-col flex-1 overflow-hidden">
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={() => setAnchorEl(null)}
        channel={channel}
      />
      <div
        id="messagesList"
        className="flex flex-col flex-1 p-5 overflow-y-auto"
        style={{ backgroundImage: `url(${whatsBackground})` }}
        onScroll={handleScroll}
      >
        {loading && (
          <div className="flex justify-center py-2">
            <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin opacity-70" />
          </div>
        )}
        {renderMessages()}
        {pendingMessages.filter(m => m.ticketId === +ticketId).map(msg => (
          <div key={msg.id} className="group relative mt-0.5 max-w-[75%] min-w-[100px] self-end ml-5">
            <div className="rounded-2xl px-3 pt-2 pb-1.5 shadow-sm bg-indigo-600 text-white rounded-tr-sm opacity-80">
              <div className="relative pr-14">
                <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                  <MarkdownWrapper>{msg.body}</MarkdownWrapper>
                </div>
                <span className="absolute bottom-0 right-0 text-[10px] flex items-center gap-0.5 text-indigo-200">
                  {format(parseISO(msg.createdAt), "HH:mm")}
                  <Clock className="w-3 h-3 inline ml-1 text-indigo-300" />
                </span>
              </div>
            </div>
          </div>
        ))}
        <div ref={lastMessageRef} style={{ float: "left", clear: "both" }} />
      </div>
    </div>
  );
};

export default MessagesList;
